import '@adornis/authentication/saml-client/client/saml-federation-login.js';
import { logger } from '@adornis/base/logging.js';
import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { config } from '@adornis/buchungsheld-db/config.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { type BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { event } from '@adornis/chemistry/decorators/event.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import '@adornis/forms/x-input.js';
import { searchParamsSignal } from '@adornis/router/client/routing-state-signal.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import '@adornis/user-forms/x-login-input.js';
import { subscribeCurrentUser, updateCurrentUser } from '@adornis/users/client/currentUser.js';
import { loginWithPassword } from '@adornis/users/db/a-user.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { type BaseError } from '@adornis/validation/errors/BaseError.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import './bh-component-action-otp-login.js';

/**
 * @element bh-login
 */
@customElement('bh-login')
export class BHLogin extends ChemistryLitElement {
  @event @property() mode?:
    | 'Login_only_with_mail'
    | 'login_with_ldap'
    | 'token_validation'
    | 'login_lecturer_with_mail';
  @property({ type: Boolean, attribute: 'hide-back-button' }) hideBackButton?: boolean;

  @state() private readonly _institutionID = new RXController(this, routingState.searchParams.get('institution'));
  @state() private readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  @state() private errorMessage = '';

  @state() private _email = '';
  password = '';

  private get _hasExtraLoginMethods() {
    return config.get('LDAP_IMPLEMENTATION') || config.get('SAML_IMPLEMENTATION');
  }

  override connectedCallback() {
    super.connectedCallback();

    const errorMessage = routingState.searchParams.getOnce('error_message');
    if (errorMessage === 'non_saml_account_already_exists') {
      XSnackbar.show(
        "Diese E-Mail Adresse ist bereits einem 'nicht-Shibboleth' Konto zugeordnet. Bitte melden Sie sich mit ihrer E-Mail Adresse an",
      );
      return;
    }

    if (!this._hasExtraLoginMethods) this.mode = 'Login_only_with_mail';
    if (searchParamsSignal.value.get('error_message'))
      this.errorMessage = searchParamsSignal.value.get('error_message')!;
  }
  clear() {
    this.mode = undefined;
    this._email = '';
  }

  override render() {
    if (!this.mode) return this._selectLoginMethod();

    return html`
      <x-flex space="md">
        ${(() => {
          let text = this._translation.translate('LOGIN_PAGE', 'loginWithMail');
          switch (this.mode) {
            case 'login_with_ldap': {
              text = this._translation.translate('LOGIN_PAGE', 'loginWithLDAP');
              break;
            }
            default: {
              // mode = token_validation
              return nothing;
            }
          }
          return html`
            <x-h1 flex ${css({ textAlign: 'center', padding: '30px', maxWidth: '20ch', margin: 'auto' })}>${text}</x-h1>
          `;
        })()}
        ${choose(
          this.mode,
          [
            ['Login_only_with_mail', () => this.renderLoginOnlyWithMail()],
            ['login_lecturer_with_mail', () => this.renderLoginLecturer()],
            ['login_with_ldap', () => this.renderLoginWithLdap()],
            ['token_validation', () => this.renderValidateToken()],
          ],
          () => this._selectLoginMethod(),
        )}
        ${!this.hideBackButton
          ? html`
              <x-button
                mode="outline"
                icon="west"
                @click=${() => {
                  this.mode = undefined;
                }}
              >
                ${this._translation.translate('LOGIN_PAGE', 'back')}
              </x-button>
            `
          : nothing}
      </x-flex>
    `;
  }

  protected _selectLoginMethod() {
    const saml = () => {
      const href = location.href.replace('step=auth', 'step=personal');
      const usePopup = config.get('SAML_USE_POPUP') || location.origin !== new URL(baseConfig.get('ROOT_URL')).origin;
      return html`
        <saml-federation-login
          .preselectedIdP=${config.get('SAML_PRESELECTED_IDP')}
          .successRedirect=${new URL(href, baseConfig.get('ROOT_URL')).href}
          ?use-popup=${usePopup}
          @login-state-changed=${(
            event: CustomEvent<{ success: true; error: null } | { success: false; error: Error }>,
          ) => {
            if (event.detail.success) {
              routingState.searchParams.set('step', 'personal');
            } else {
              this.errorMessage = event.detail.error?.message;
            }
          }}
        ></saml-federation-login>
        <x-hr></x-hr>
      `;
    };
    return html`
      <x-flex space-between space="lg" w100 crossaxis-center>
        <x-flex space="lg" flex>
          <x-h1 flex ${css({ textAlign: 'center', padding: '30px', maxWidth: '20ch', margin: 'auto' })}
            >${this._translation.translate('LOGIN')}</x-h1
          >
          ${this.errorMessage ? html` <x-text tone="error"> ${this.errorMessage} </x-text> ` : nothing}
          ${config.get('SAML_IMPLEMENTATION') // deactivate saml login for now
            ? saml()
            : nothing}
          ${config.get('LDAP_IMPLEMENTATION') && false // deactivate ldap login for now
            ? html`
                <x-button @click=${() => (this.mode = 'login_with_ldap')}>
                  ${this._translation.translate('LOGIN_PAGE', 'loginWithLDAP')}
                </x-button>
              `
            : nothing}

          <tr-button @click=${() => (this.mode = 'Login_only_with_mail')}>
            ${this._translation.translate('LOGIN_PAGE', 'loginWithMail')}
          </tr-button>
        </x-flex>
      </x-flex>
    `;
  }

  loginWithMailRenderHelper({
    withoutPassword = false,
    emailOnlyLoginCallback,
    emailAndPasswordLoginCallback,
  }: {
    withoutPassword?: boolean;
    emailOnlyLoginCallback?: (email: string) => Promise<any>;
    emailAndPasswordLoginCallback: (email: string, password: string) => Promise<any>;
  }) {
    if (withoutPassword && !emailOnlyLoginCallback)
      throw new Error('Cannot use login render helper without password but not provide an email only login callback');

    let email = '';

    const validateEmailAndSetError = (email: string) => {
      if (!emailRegexCheck(email)) {
        logger.error({ email }, 'Did not enter valid mail address');
        this._mailError = this._translation.translate('LOGIN_PAGE', 'enterValidMailAddress');
        this.requestUpdate();
        return false;
      }
      return true;
    };

    return html`
      <x-flex space="lg" w100 crossaxis-center>
        <x-flex space="sm" ${css({ width: '250px' /* hardcoded width for x-overlay */ })}>
          <tr-login-input
            type="email"
            id="username"
            icon="mail"
            placeholder=${this._translation.translate('field', 'email')}
            @value-changed=${(e: ValueEvent<string>) => {
              if (this._email) {
                this._email = e.detail.value;
              }
              email = e.detail.value;
            }}
            @submit=${() => {
              if (!this._email) {
                if (!validateEmailAndSetError(email)) return;
                this._email = email;
              } else {
                emailAndPasswordLoginCallback(this._email, this.password);
              }
            }}
          ></tr-login-input>

          ${this._email
            ? html`
                <tr-login-input
                  type="password"
                  id="password"
                  icon="password"
                  placeholder=${this._translation.translate('field', 'password')}
                  @value-changed=${(e: ValueEvent<string>) => (this.password = e.detail.value)}
                  @submit=${() => emailAndPasswordLoginCallback(this._email, this.password)}
                ></tr-login-input>
              `
            : nothing}
          ${this._mailError ? html` <x-text tone="error" ${css({})}> ${this._mailError} </x-text> ` : nothing}
          ${this.errorMessage ? html` <x-text tone="error" ${css({})}> ${this.errorMessage} </x-text> ` : nothing}
          ${this._email
            ? html`
                <tr-button @click=${() => emailAndPasswordLoginCallback(this._email, this.password)}>
                  ${this._translation.translate('LOGIN')}
                </tr-button>
              `
            : html`
                <tr-button
                  @click=${() => {
                    if (!validateEmailAndSetError(email)) return;
                    this._email = email;
                  }}
                >
                  ${this._translation.translate('LOGIN_PAGE', 'next')}
                </tr-button>
              `}
          ${withoutPassword
            ? html`
                <tr-button
                  mode="outline"
                  icon="mail"
                  @click=${() => {
                    const _email = this._email || email;
                    if (!validateEmailAndSetError(_email)) return;
                    emailOnlyLoginCallback!(_email);
                  }}
                >
                  ${this._translation.translate('LOGIN_PAGE', 'loginWithoutPassword')}
                </tr-button>
              `
            : nothing}

          <!-- same as loginWithoutPassword; This is for users who really want to reset their password and do not understand that they could use the loginWithoutPassword button -->
          <tr-button
            mode="outline"
            icon="mail"
            @click=${() => {
              const _email = this._email || email;
              if (!validateEmailAndSetError(_email)) return;
              emailOnlyLoginCallback!(_email);
            }}
          >
            ${this._translation.translate('LOGIN_PAGE', 'forgotPassword')}
          </tr-button>
        </x-flex>
      </x-flex>
    `;
  }

  private _mailError = '';
  renderLoginOnlyWithMail() {
    return this.loginWithMailRenderHelper({
      withoutPassword: true,
      emailOnlyLoginCallback: (email: string) => this._loginWithEmailOnly(email),
      emailAndPasswordLoginCallback: (email: string, password: string) => {
        return this._loginWithPassword(email, password, 'adornis');
      },
    });
  }

  renderLoginWithLdap() {
    return this.loginWithMailRenderHelper({
      withoutPassword: false,
      emailAndPasswordLoginCallback: (email: string, password: string) =>
        this._loginWithPassword(email, password, 'ldap'),
    });
  }

  renderValidateToken() {
    return html`
      <bh-component-action-otp-login
        email=${this._email}
        institution-id=${ifDefined(this._institutionID.value)}
        @login=${() => this._login()}
      ></bh-component-action-otp-login>
    `;
  }

  renderLoginLecturer() {
    return this.loginWithMailRenderHelper({
      withoutPassword: true,
      emailOnlyLoginCallback: (email: string) => this._loginWithEmailOnlyLecturer(email),
      emailAndPasswordLoginCallback: (email: string, password: string) => {
        return this._loginWithPassword(email, password, 'adornis');
      },
    });
  }

  private async _loginWithEmailOnly(email?: string | null) {
    email = email ?? this._email;
    // TODO: validate email

    const userExists = await User.getUserByEmailExists(email)();

    if (!userExists) {
      // create new user and log in
      // TODO FIXME: think of soemthing better
      const user = new User({ email, username: email, firstName: email.split('@')[0], lastName: '-' });
      await user.create();
    }

    this.mode = 'token_validation';
    this._email = email;
  }

  private async _loginWithEmailOnlyLecturer(email?: string | null) {
    email = email ?? this._email;
    // TODO: validate email

    const userExists = await User.getUserByEmailExists(email)();

    if (!userExists) {
      // create new user and log in
      // TODO FIXME: think of soemthing better
      const user = new User({
        email,
        username: email,
        firstName: email.split('@')[0],
        lastName: '-',
      });
      user.roles.push(
        new AdornisRoleToContextsWrapper({
          _id: '672115c384f767880f9aa361',
          name: 'lecturer',
          contexts: ['testing_institution_1'],
        }),
      );
      const userId = await user.create();

      // For some reason, does not find itself here, and cannot do the update.
      await User.ensureSelfRoles(userId);
      // let response = await user.addRole('lecturer', []);
      // console.log('----------------------------------------');
      // console.log(response);
    }

    this.mode = 'token_validation';
    this._email = email;
  }

  private async _loginWithPassword(email: string, password: string, method: 'ldap' | 'adornis') {
    // TODO: validate email
    // TODO: validate password

    this.errorMessage = '';

    if (
      !(await TrDialog.waitFor(
        new Promise<boolean>(async resolve => {
          try {
            switch (method) {
              case 'ldap':
                await this._handleLDAPLogin(email, password);
                break;
              case 'adornis':
                await loginWithPassword(email, password, User)();
                break;
              default:
                throw new Error('Invalid login with password method: ' + method);
            }
          } catch (error) {
            const key = (error as BaseError).extensions.translationKey;
            this.errorMessage =
              key === 'invalid_credentials' || key === 'users.invalid_credentials'
                ? this._translation.translate('LOGIN_PAGE', 'emailOrPasswordWrong')
                : this._translation.translate('LOGIN_PAGE', 'invalidInput');
            return resolve(false); // exit on fail
          }

          updateCurrentUser(undefined, false);
          await subscribeCurrentUser();
          resolve(true);
        }),
      ))
    )
      return;

    // user should be logged in now
    this._login();
  }

  private async _handleLDAPLogin(email: string, password: string) {
    await User.loginWithLDAP(email, password)();
    updateCurrentUser(undefined, false);
    await subscribeCurrentUser();
    window.location.reload(); // for some reason the workshop-order process only works after reloading once
  }

  private _login() {
    this.dispatchEvent(new CustomEvent('login'));
  }
}
